import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import ReactDatatable from "@mkikets/react-datatable";
import { useGetUsersWatchPointsHistoryQuery, useGetAllUsersListQuery } from "../../../redux/userApi";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Button, Row, Col, Form } from "react-bootstrap";
import Select from 'react-select';

function UserWatchPoints() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { t } = useTranslation();

    const initialPage = Number(searchParams.get('page')) || 1;
    const initialLimit = Number(searchParams.get('limit')) || 10;
    const initialUserId = searchParams.get('user_id') || id || "";
    const initialStartDate = searchParams.get('start_date') || "";
    const initialEndDate = searchParams.get('end_date') || "";

    const [params, setParams] = useState({
        page: initialPage,
        limit: initialLimit,
        user_id: initialUserId,
        start_date: initialStartDate,
        end_date: initialEndDate
    });

    const [filters, setFilters] = useState({
        user_id: initialUserId,
        start_date: initialStartDate,
        end_date: initialEndDate
    });

    useEffect(() => {
        const urlParams = new URLSearchParams();
        if (params.page !== 1) urlParams.set('page', params.page);
        if (params.limit !== 10) urlParams.set('limit', params.limit);
        if (params.user_id) urlParams.set('user_id', params.user_id);
        if (params.start_date) urlParams.set('start_date', params.start_date);
        if (params.end_date) urlParams.set('end_date', params.end_date);
        
        setSearchParams(urlParams, { replace: true });
    }, [params, setSearchParams]);

    useEffect(() => {
        if (!location.search && (params.page !== 1 || params.user_id || params.start_date || params.end_date)) {
            const resetState = {
                page: 1,
                limit: 10,
                user_id: "",
                start_date: "",
                end_date: ""
            };
            setParams(resetState);
            setFilters(resetState);
        }
    }, [location.search]);

    const { data: apiResponse, isLoading, isFetching } = useGetUsersWatchPointsHistoryQuery(params);
    const historyData = apiResponse?.data || [];
    const totalRecords = apiResponse?.totalCount || 0;
    const totalPoints = apiResponse?.totalPoints || 0;

    const { data: userResponse } = useGetAllUsersListQuery();
    const users = userResponse?.data || [];

    const userOptions = [
        { value: "", label: t('select_user') || "All Users" },
        ...users.map(user => ({
            value: user.id,
            label: String(user.mobile || user.email || user.fname || user.id)
        }))
    ];

    const customStyles = {
        control: (provided) => ({ ...provided, color: '#000' }),
        singleValue: (provided) => ({ ...provided, color: '#000' }),
        input: (provided) => ({ ...provided, color: '#000' }),
        option: (provided, state) => ({
            ...provided,
            color: '#000',
            backgroundColor: state.isFocused ? '#e9ecef' : '#fff',
        }),
        menu: (provided) => ({ ...provided, backgroundColor: '#fff' })
    };

    const handleUserSelect = (selectedOption) => {
        setFilters(prev => ({ ...prev, user_id: selectedOption ? selectedOption.value : "" }));
    };

    const handleSearchSubmit = () => {
        setParams({
            ...params,
            page: 1,
            user_id: filters.user_id,
            start_date: filters.start_date,
            end_date: filters.end_date
        });
    };

    const handleClearFilters = () => {
        setFilters({ user_id: "", start_date: "", end_date: "" });
        setParams({ page: 1, limit: 10, user_id: "", start_date: "", end_date: "" });
    };

    const handleTableChange = (newParams) => {
        setParams({
            ...params,
            page: newParams.page_number,
            limit: newParams.page_size,
        });
    };

    const columns = [
        {
            key: "srno.",
            text: t('sr_no') || "Sr No.",
            className: "sr_no.",
            align: "left",
            sortable: true,
            cell: (row, index) => (params.page - 1) * params.limit + index + 1,
        },
        {
            key: "user",
            text: t('user') || "User (Email/Phone)",
            className: "user",
            align: "left",
            sortable: true,
            cell: (record) => (
                <div>
                    <div>{record.user_email || '-'}</div>
                    <div>{record.user_mobile || '-'}</div>
                </div>
            )
        },
        {
            key: "ads_points_setting_id",
            text: t('ad_setting_id') || "Ad Setting ID",
            className: "ad_setting_id",
            align: "left",
            sortable: true,
            cell: (record) => record.ads_points_setting_id || "-"
        },
        {
            key: "ads_points_setting_number",
            text: t('ad_number') || "Ad Number",
            className: "ad_number",
            align: "left",
            sortable: true,
            cell: (record) => record.ads_points_setting_number || "0"
        },
        {
            key: "points",
            text: t('points') || "Points",
            className: "points",
            align: "left",
            sortable: true,
            cell: (record) => (
                <span className="fw-bold text-success">
                    {record.points || "0"}
                </span>
            )
        },
        {
            key: "status",
            text: t('status') || "Status",
            className: "status",
            align: "left",
            sortable: true,
            cell: (record) => (
                <span className={`badge bg-${record.status === 'active' ? 'success' : 'danger'}`}>
                    {record.status}
                </span>
            )
        },
        {
            key: "created_at",
            text: t('created_at') || "Date",
            className: "created_at",
            align: "left",
            sortable: true,
            cell: (record) => {
                const date = record.created_at;
                return <>{date ? moment(date).format("DD/MM/YYYY HH:mm:ss") : ""}</>;
            },
        }
    ];

    const config = {
        page_size: 10,
        length_menu: [10, 20, 50],
        filename: "User Watch Points List",
        no_data_text: t('no_record_found') || "No records found",
        language: {
            filter: t('search_in_records') || "Search in records...",
            pagination: {
                first: t('first') || "First",
                previous: t('previous') || "Previous",
                next: t('next') || "Next",
                last: t('last') || "Last",
            },
        },
        show_length_menu: true,
        show_filter: false,
        show_pagination: true,
        show_info: true,
    };

    return (
        <>
            <style>{`
                .date-picker-input::-webkit-calendar-picker-indicator {
                    filter: invert(1);
                    cursor: pointer;
                }
            `}</style>
            <section className="section dashboard">
                <div className="pagetitle d-flex align-items-center gap-2 mb-4">
                <Button variant="secondary" onClick={() => navigate(-1)}>
                    {t('back') || "Back"}
                </Button>
                <h1>{t('user_watch_points_history') || "User Watch Points History"}</h1>
            </div>

            <div className="card mb-4">
                <div className="card-body pt-4">
                    <Row className="mb-3 align-items-end">
                        <Col md={3} className="mb-3 mb-md-0">
                            <Form.Group controlId="filterUserId">
                                <Form.Label>{t('user_id') || "Select User"}</Form.Label>
                                <Select
                                    options={userOptions}
                                    value={userOptions.find(opt => String(opt.value) === String(filters.user_id)) || userOptions[0]}
                                    onChange={handleUserSelect}
                                    isSearchable
                                    styles={customStyles}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2} className="mb-3 mb-md-0">
                            <Form.Group controlId="filterStartDate">
                                <Form.Label>{t('start_date') || "Start Date"}</Form.Label>
                                <Form.Control 
                                    type="date" 
                                    className="date-picker-input"
                                    value={filters.start_date}
                                    onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                    style={{ cursor: "pointer" }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2} className="mb-3 mb-md-0">
                            <Form.Group controlId="filterEndDate">
                                <Form.Label>{t('end_date') || "End Date"}</Form.Label>
                                <Form.Control 
                                    type="date" 
                                    className="date-picker-input"
                                    value={filters.end_date}
                                    onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                    style={{ cursor: "pointer" }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2} className="d-flex align-items-end gap-2 mb-3 mb-md-0">
                            <Button variant="primary" onClick={handleSearchSubmit}>
                                {t('search') || "Search"}
                            </Button>
                            <Button variant="secondary" onClick={handleClearFilters}>
                                {t('clear') || "Clear"}
                            </Button>
                        </Col>
                        <Col md={3} className="text-md-end text-start mt-3 mt-md-0">
                            <div className="d-inline-flex align-items-center bg-light border rounded px-3 py-2">
                                <div className="me-2 text-primary fs-4">
                                    <i className="bi bi-play-circle-fill"></i>
                                </div>
                                <div>
                                    <div className="text-muted small fw-bold">{t('total_points') || 'Total Points'}</div>
                                    <div className="fs-5 fw-bold text-dark lh-1">{totalPoints}</div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            <div className="card">
                <div className="card-body pt-4 position-relative">
                    {(isLoading || isFetching) && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 10 }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                    <ReactDatatable
                        key={`datatable-user-watch-points-${params.user_id}-${params.start_date}-${params.end_date}`}
                        config={config}
                        records={historyData}
                        columns={columns}
                        dynamic={true}
                        total_record={totalRecords}
                        onChange={handleTableChange}
                    />
                </div>
            </div>
        </section>
        </>
    );
}

export default UserWatchPoints;
